import PropTypes from "prop-types";
import React, { useMemo, useRef, useState } from "react";
import algoliasearch from "algoliasearch/lite";
import { Configure, InstantSearch, SearchBox, RefinementList } from "react-instantsearch";

import PrProfileCardSkeleton from "./card/PrProfileCardSkeleton";
import InfiniteHits from "./search/InfiniteHits";
import GeoSearchByPlace from "../algolia_search/geo_search_by_place";
import SearchToolbar from "../source_directory/SearchToolbar";
import FilterPill from "../source_directory/FilterPill";
import FilterClearEffect from "../source_directory/FilterClearEffect";
import sharedAlgoliaRouter from "../source_directory/sharedAlgoliaRouter";
import useSavedOnlyFilter from "../source_directory/useSavedOnlyFilter";
import useOwnedLocationParams from "../source_directory/useOwnedLocationParams";
import useDebouncedQueryHook from "../source_directory/useDebouncedQueryHook";

const OWN_TAB = "pr_profiles";

function PrProfiles({
  algoliaAppId,
  algoliaSearchKey,
  indexName,
  viewMode,
  showFilters,
  onToggleFilters,
  savedOnly,
  onToggleSavedOnly,
  onViewModeChange,
  groupCount,
  onMyGroupsClick,
  initialPathname = undefined,
}) {
  const queryHook = useDebouncedQueryHook();
  const searchClient = useMemo(() => algoliasearch(algoliaAppId, algoliaSearchKey), [algoliaAppId, algoliaSearchKey]);
  const savedFilter = useSavedOnlyFilter(savedOnly, "Id::Profile::Promoter");

  const getSearchParamsFromUrl = () => {
    const url = new URL(window.location.href);
    return {
      query: url.searchParams.get("query"),
      hashtag: url.searchParams.get("hashtag"),
      title: url.searchParams.get("title"),
      workplace_name: url.searchParams.get("workplace_name"),
    };
  };

  // Rewrite friendly deep-link params into Algolia's routing format synchronously during the first
  // render, before the child InstantSearch reads the URL on mount. A useEffect would run too late.
  const didTransformUrl = useRef(false);
  if (!didTransformUrl.current) {
    didTransformUrl.current = true;
    const currentUrl = new URL(window.location.href);
    const hasAlgoliaParams = Array.from(currentUrl.searchParams.keys()).some((key) => key.includes(`${indexName}[`));
    const searchParams = getSearchParamsFromUrl();
    const paramsToTransform = ["query", "hashtag", "title", "workplace_name"];
    const needsTransformation = paramsToTransform.some((param) => searchParams[param] !== null);

    if (needsTransformation && !hasAlgoliaParams) {
      const newUrl = new URL(window.location.href);
      // Drop only the consumed legacy params — anything else in the URL (saved=1, another
      // tab's filter state) is not this transform's to discard.
      paramsToTransform.forEach((param) => newUrl.searchParams.delete(param));
      if (searchParams.query) newUrl.searchParams.set(`${indexName}[query]`, searchParams.query);
      if (searchParams.hashtag)
        newUrl.searchParams.set(`${indexName}[refinementList][hashtags.hashtag_name][0]`, searchParams.hashtag);
      if (searchParams.title)
        newUrl.searchParams.set(`${indexName}[refinementList][current_position_title][0]`, searchParams.title);
      if (searchParams.workplace_name)
        newUrl.searchParams.set(
          `${indexName}[refinementList][current_position_workplace][0]`,
          searchParams.workplace_name,
        );
      window.history.replaceState(null, "", newUrl.toString());
    }
  }

  const { locationName, locationRadius } = useOwnedLocationParams(initialPathname, OWN_TAB);
  const [locationActive, setLocationActive] = useState(!!locationName);
  const [locationLabel, setLocationLabel] = useState(locationName ?? null);
  const [locationResetSignal, setLocationResetSignal] = useState(0);

  return (
    <div className="ais-InstantSearch">
      <InstantSearch
        indexName={indexName}
        searchClient={searchClient}
        routing={{ router: sharedAlgoliaRouter }}
        future={{ preserveSharedStateOnUnmount: true, cleanUrlOnDispose: false }}
      >
        <Configure maxValuesPerFacet={1000} filters={savedFilter ?? ""} />
        <FilterClearEffect
          showFilters={showFilters}
          onClear={() => {
            setLocationActive(false);
            setLocationLabel(null);
            setLocationResetSignal((signal) => signal + 1);
          }}
        />
        <SearchToolbar
          showFilters={showFilters}
          onToggleFilters={onToggleFilters}
          savedOnly={savedOnly}
          onToggleSavedOnly={onToggleSavedOnly}
          viewMode={viewMode}
          onViewModeChange={onViewModeChange}
          groupCount={groupCount}
          onMyGroupsClick={onMyGroupsClick}
        >
          <SearchBox autoFocus placeholder="Search" searchAsYouType queryHook={queryHook} />
        </SearchToolbar>

        <div
          className="sd-filter-row"
          style={{ display: showFilters ? "flex" : "none", flexWrap: "wrap", gap: 8, marginBottom: 12 }}
        >
          <FilterPill label="Title" attribute="current_position_title">
            <RefinementList attribute="current_position_title" limit={10} showMore searchable />
          </FilterPill>
          <FilterPill label="Company" attribute="current_position_workplace">
            <RefinementList attribute="current_position_workplace" limit={10} showMore searchable />
          </FilterPill>
          {/* Also the target of profile-page tag chips (/pr_profiles?hashtag=...): the rewritten
              refinement only applies while a widget for this attribute is mounted. */}
          <FilterPill label="Tags" attribute="hashtags.hashtag_name">
            <RefinementList attribute="hashtags.hashtag_name" limit={10} showMore searchable />
          </FilterPill>
          <FilterPill label="Location" active={locationActive} activeText={locationLabel}>
            <div style={{ minWidth: 220 }}>
              <GeoSearchByPlace
                initialLocationName={locationName}
                initialLocationRadius={locationRadius}
                onActiveChange={setLocationActive}
                onLabelChange={setLocationLabel}
                resetSignal={locationResetSignal}
              />
            </div>
          </FilterPill>
        </div>

        <InfiniteHits PrProfileCardSkeleton={PrProfileCardSkeleton} viewMode={viewMode} />
      </InstantSearch>
    </div>
  );
}

PrProfiles.propTypes = {
  algoliaAppId: PropTypes.string.isRequired,
  algoliaSearchKey: PropTypes.string.isRequired,
  indexName: PropTypes.string.isRequired,
  viewMode: PropTypes.oneOf(["grid", "list"]).isRequired,
  showFilters: PropTypes.bool.isRequired,
  onToggleFilters: PropTypes.func.isRequired,
  savedOnly: PropTypes.bool,
  onToggleSavedOnly: PropTypes.func.isRequired,
  onViewModeChange: PropTypes.func.isRequired,
  groupCount: PropTypes.number,
  onMyGroupsClick: PropTypes.func,
  initialPathname: PropTypes.string,
};

export default PrProfiles;
